import React, { useState, useEffect, useCallback } from 'react';
import { Form, Tabs, message } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useSocketContext } from '../../../sockets/SocketContext';
import { setLastSearchQuery, setFormValues } from '../../../store/tz/reducer';
import { setWipoLastSearchQuery } from '../../../store/wipoSlice';
import normalizeMktu from '../../../utils/normalizeMktu';
import { WIPO_FIELD_LABELS } from './fieldLabels';
import ResultsGrid from '../ext/ResultsGrid';
import WipoResultsGrid from '../WIPO/WipoResultsGrid';
import SearchForm from './SearchForm';

/**
 * Конструктор поиска: запросник (SearchForm) + вкладки результатов (ТЗ / Заявки / ВИПО).
 * Формат данных: description с одним из ключей konstruktor | konstruktor_req | konstruktor_wipo.
 */
const KonstruktorResults = () => {
  const { createNewTaskOnServer } = useSocketContext();
  const { results, loading, error } = useSelector((s) => s.search);
  const { forms } = useSelector((s) => s.tz);
  const wipoLastSearchQuery = useSelector((s) => s.wipo.lastSearchQuery);
  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('tz');
  const [queryString, setQueryString] = useState('');

  const savedKonstruktor = forms?.konstruktor;

  const pushKonstruktorToRedux = useCallback(() => {
    dispatch(
      setFormValues({ formKey: 'konstruktor', values: form.getFieldsValue(true) }),
    );
  }, [dispatch, form]);

  useEffect(() => {
    if (savedKonstruktor) {
      form.setFieldsValue(savedKonstruktor);
    }
  }, [form, savedKonstruktor]);

  const onFinish = (values) => {
    const { text, selectedFields, quantity, filters, scope, mktu, isPhonetic } = values;
    if (!selectedFields?.length) return message.error('Выберите поля');

    setQueryString(text);
    dispatch(setLastSearchQuery(text));
    const normalizedMktu = normalizeMktu(mktu);

    const wipoFieldKeys = Object.keys(WIPO_FIELD_LABELS);
    const fields =
      scope === 'wipo'
        ? selectedFields
            .map((f) => (f === 'realName' ? 'Trademark' : f))
            .filter((f) => wipoFieldKeys.includes(f))
        : selectedFields;

    if (!fields.length) return message.error('Для ВИПО выберите хотя бы одно поле из списка');

    const rawFilters = (filters || []).filter((f) => f?.key && f?.value);
    const filtersForScope =
      scope === 'wipo'
        ? rawFilters.map((f) => ({ ...f, key: f.key === 'realName' ? 'Trademark' : f.key }))
            .filter((f) => wipoFieldKeys.includes(f.key))
        : rawFilters;

    const baseDescription = {
      quantity: quantity ?? 20,
      fields,
      filters: filtersForScope,
    };
    if (Array.isArray(normalizedMktu) && normalizedMktu.length > 0) {
      baseDescription.mktu = normalizedMktu;
    }
    if (isPhonetic) {
      baseDescription.is_phonetic = true;
    }

    if (scope === 'tz') {
      const desc = { ...baseDescription, konstruktor: text };
      createNewTaskOnServer(desc, 'konstruktor', (e) =>
        e ? message.error('Ошибка задачи ТЗ') : message.success('Задача ТЗ создана'),
      );
    } else if (scope === 'requests') {
      const desc = { ...baseDescription, konstruktor_req: text };
      createNewTaskOnServer(desc, 'konstruktor_req', (e) =>
        e ? message.error('Ошибка задачи заявок') : message.success('Задача заявок создана'),
      );
    } else {
      dispatch(setWipoLastSearchQuery(text));
      const desc = { ...baseDescription, konstruktor_wipo: text };
      createNewTaskOnServer(desc, 'konstruktor_wipo', (e) =>
        e ? message.error('Ошибка задачи ВИПО') : message.success('Задача ВИПО создана'),
      );
    }
  };

  const items = [
    {
      key: 'tz',
      label: 'ТЗ',
      children: (
        <ResultsGrid
          items={results.konstruktor}
          loading={loading.konstruktor}
          error={error.konstruktor}
          loadingTip="Поиск ТЗ…"
          errorTitle="Ошибка ТЗ"
          emptyTitle="ТЗ не найдены"
          requestType="Конструктор (ТЗ)"
          searchQuery={queryString}
        />
      ),
    },
    {
      key: 'requests',
      label: 'Заявки',
      children: (
        <ResultsGrid
          items={results.konstruktor_req}
          loading={loading.konstruktor_req}
          error={error.konstruktor_req}
          loadingTip="Поиск заявок…"
          errorTitle="Ошибка заявок"
          emptyTitle="Заявок не найдено"
          requestType="Конструктор (заявки)"
          searchQuery={queryString}
        />
      ),
    },
    {
      key: 'wipo',
      label: 'ВИПО',
      children: (
        <WipoResultsGrid
          items={results.konstruktor_wipo}
          loading={loading.konstruktor_wipo}
          error={error.konstruktor_wipo}
          loadingTip="Поиск ВИПО…"
          errorTitle="Ошибка ВИПО"
          emptyTitle="Не найдено"
          requestType="Конструктор (ВИПО)"
          searchQuery={wipoLastSearchQuery || queryString}
        />
      ),
    },
  ];

  return (
    <div style={{ width: '100%', padding: 16 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={() => pushKonstruktorToRedux()}
      >
        <SearchForm
          activeTab={activeTab}
          onScopeChange={(val) => {
            setActiveTab(val);
            form.setFieldsValue({ scope: val, selectedFields: [], filters: [] });
            Promise.resolve().then(() => pushKonstruktorToRedux());
          }}
        />
      </Form>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          form.setFieldsValue({ scope: key, selectedFields: [], filters: [] });
          Promise.resolve().then(() => pushKonstruktorToRedux());
        }}
        destroyInactiveTabPane={false}
        items={items}
      />
    </div>
  );
};

export default KonstruktorResults;
